import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle2, CreditCard, LoaderCircle, ShieldCheck, Sparkles, X, Zap } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { OrderView, ProductQuote } from "../../shared/contracts";
import { formatAmount, getProduct, PRODUCTS, type ProductCode } from "../../shared/products";
import { api, ApiClientError } from "../api";
import { useAuth } from "../auth";
import { useCapabilities } from "../capabilities";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PageHeader } from "../components/PageHeader";

type CheckoutState = Awaited<ReturnType<typeof api.createOrder>>;

const PAYMENT_QR_LOGO_URL =
  "https://img.kookapp.cn/mf-assets/v1/mall_new/static/media/mode2.5174d005825b319b.png";

export function PricingPage() {
  const { me, requestLogin } = useAuth();
  const { fubeiEnabled } = useCapabilities();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<{ userId: string; quote: ProductQuote } | null>(null);
  const [checkout, setCheckout] = useState<CheckoutState | null>(null);
  const catalog = useQuery({ queryKey: ["products"], queryFn: api.products, retry: false });
  const quotes = useQuery({
    queryKey: ["product-quotes", me?.user.id],
    queryFn: api.productQuotes,
    enabled: Boolean(me),
    retry: false,
    staleTime: 0,
  });
  const purchase = useMutation({
    mutationFn: api.createOrder,
    onSuccess: (result) => {
      setConfirming(null);
      setCheckout(result);
      sessionStorage.setItem("gameframe:last-order", JSON.stringify({
        id: result.order.id, outTradeNo: result.order.outTradeNo,
      }));
      void api.track("checkout_started", { amountFen: result.order.amountFen, mode: "wap" });
    },
    onError: (error) => {
      if (error instanceof ApiClientError && ["ORDER_PRICE_CHANGED", "ENTITLEMENT_ALREADY_ACTIVE"].includes(error.code)) {
        setConfirming(null);
        void queryClient.invalidateQueries({ queryKey: ["product-quotes"] });
        void queryClient.invalidateQueries({ queryKey: ["me"] });
      }
    },
  });
  const order = useQuery({
    queryKey: ["order", checkout?.order.outTradeNo],
    queryFn: () => api.orderByTradeNo(checkout!.order.outTradeNo),
    enabled: Boolean(checkout),
    retry: false,
    refetchInterval: (query) => !query.state.data || query.state.data.status === "PENDING" ? 1_500 : false,
  });
  const status = order.data?.status ?? checkout?.order.status;
  const selected = confirming ? getProduct(confirming.quote.productCode) : undefined;

  useEffect(() => {
    setConfirming(null);
    setCheckout(null);
  }, [me?.user.id]);

  useEffect(() => {
    if (status === "PAID" || status === "REFUNDED") {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      void queryClient.invalidateQueries({ queryKey: ["product-quotes"] });
    }
  }, [queryClient, status]);

  useEffect(() => {
    if (!quotes.data?.planEndsAt) return;
    const remaining = Date.parse(quotes.data.planEndsAt) - Date.now();
    const timer = window.setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ["product-quotes"] });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    }, Math.min(Math.max(remaining + 100, 100), 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [queryClient, quotes.data?.planEndsAt, quotes.dataUpdatedAt]);

  const startPurchase = (productCode: ProductCode) => {
    if (!fubeiEnabled) return;
    purchase.reset();
    void api.track("pricing_viewed", { source: "pricing_page" });
    if (!me) return requestLogin("/pricing");
    const quote = quotes.data?.items.find((item) => item.productCode === productCode);
    if (quote?.purchasable && !quotes.isError) setConfirming({ userId: me.user.id, quote });
  };

  return (
    <section>
      <PageHeader eyebrow="Creation Access" title="为灵感持续充能"
        subtitle="体验版与两档创作者权益，一次性付款，不自动续费。" />
      <div className="plan-grid">
        <article className="plan-card">
          <span className="plan-icon"><Sparkles size={20} /></span>
          <h2>体验版</h2>
          <p>适合偶尔尝试新灵感</p>
          <div className="plan-price">¥0 <small>/ 长期可用</small></div>
          <Feature>每个自然月 10 次生成</Feature>
          <Feature>最高 1080P 下载</Feature>
          <Feature>标准生成队列</Feature>
          <button className="button button-secondary button-full" disabled={me?.user.planCode === "FREE"}>
            {me?.user.planCode === "FREE" ? "当前方案" : "免费方案"}
          </button>
        </article>
        {(catalog.data ?? PRODUCTS).map((product) => {
          const quote = me ? quotes.data?.items.find((item) => item.productCode === product.code) : undefined;
          const price = quote?.amountFen ?? product.firstPurchaseAmountFen;
          const blocked = Boolean(me && (!quote?.purchasable || quotes.isError));
          return (
            <article className="plan-card featured" key={product.code}>
              <span className="recommend">推荐方案</span>
              {quote?.isTestPayment && <span className="test-mode-chip">测试价</span>}
              <span className="plan-icon accent"><Zap size={20} /></span>
              <h2>{product.name}</h2>
              <p>{product.description}</p>
              <div className="plan-price">¥{formatAmount(price)} <small>/ {product.durationDays} 天</small></div>
              <p className="plan-price-note">
                {quote?.isTestPayment ? "测试环境实付 ¥1.00" : quote?.priceTier === "REPURCHASE" ? "本次为再次购买价" : "首购优惠"}
                {" · 再次购买 ¥" + formatAmount(product.repurchaseAmountFen) + " / 30 天"}
              </p>
              <Feature>{"有效期内 " + product.quotaTotal + " 次生成机会"}</Feature>
              <Feature>原始高清无水印下载</Feature>
              <Feature>一次性购买，不自动续费</Feature>
              <Feature>付费作品持续保留</Feature>
              <button className="button button-primary button-full"
                disabled={!fubeiEnabled || blocked || purchase.isPending}
                onClick={() => startPurchase(product.code)}>
                <CreditCard size={17} />
                {!fubeiEnabled ? "支付暂未开放" : me && quotes.isError ? "报价暂不可用"
                  : quote?.blockedReason ?? (me && !quote ? "正在获取报价" : "使用支付宝购买")}
              </button>
              <p className="plan-disclaimer">
                {!fubeiEnabled ? "支付接入配置中，体验版生成不受影响" : quote?.isTestPayment
                  ? "测试环境 · 实付 ¥1.00 · 不自动续费"
                  : "每档各享一次首购优惠 · 到期后可手动再次购买"}
              </p>
            </article>
          );
        })}
      </div>
      {me && quotes.isError && <p className="form-error centered" role="alert">
        暂时无法获取购买报价。<button className="button button-secondary button-small" onClick={() => void quotes.refetch()}>重新获取</button>
      </p>}
      <div className="payment-assurance">
        <ShieldCheck size={19} />
        <div><strong>到账提示</strong><p>支付完成后，所购 30 天权益及对应生成额度通常会立即到账。
          如页面暂未更新，请稍后刷新或前往个人中心查看，请勿重复付款。</p></div>
      </div>
      {purchase.error && <p className="form-error centered" role="alert">
        {purchase.error instanceof ApiClientError ? purchase.error.message : "订单创建失败，请稍后重试"}
      </p>}
      <ConfirmDialog open={fubeiEnabled && Boolean(confirming && confirming.userId === me?.user.id)}
        title={selected ? "确认购买" + selected.name : "确认购买"}
        description={selected && confirming ?
          `商品：${selected.name}；本次应付：¥${formatAmount(confirming.quote.amountFen, true)}；` +
          `${selected.durationDays} 天内 ${selected.quotaTotal} 次生成机会。一次性付款，不自动续费或扣款。` +
          "有效期从支付成功时起计算；如切换商品，之前的付款码将失效。" +
          (confirming.quote.isTestPayment ? " 当前为测试环境 1 元订单。" : "") : ""}
        confirmLabel="确认并生成付款码" busy={purchase.isPending}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          if (confirming && confirming.userId === me?.user.id) purchase.mutate({
            productCode: confirming.quote.productCode, expectedAmountFen: confirming.quote.amountFen,
          });
        }} />
      {checkout && <PaymentQrDialog checkout={checkout} status={status} refreshFailed={order.isError}
        testMode={checkout.order.isTestPayment} onClose={() => setCheckout(null)} />}
      <p className="pricing-legal">购买前请阅读 <Link to="/terms">用户协议</Link> 与 <Link to="/privacy">隐私政策</Link>。</p>
    </section>
  );
}

function PaymentQrDialog({
  checkout,
  status,
  refreshFailed,
  testMode,
  onClose,
}: {
  checkout: CheckoutState;
  status?: OrderView["status"];
  refreshFailed: boolean;
  testMode: boolean;
  onClose(): void;
}) {
  const paid = status === "PAID";
  const terminal = Boolean(
    status && ["CLOSED", "FAILED", "REFUND_REQUIRED", "REFUNDED"].includes(status),
  );

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className="modal checkout-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-title"
      >
        <header className="modal-header">
          <div>
            <p className="eyebrow">{testMode ? "Test payment" : "Alipay payment"}</p>
            <h2 id="checkout-title">{paid ? "支付已确认" : "请使用支付宝扫码"}</h2>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭付款码">
            <X size={19} />
          </button>
        </header>
        {paid ? (
          <div className="checkout-success">
            <span className="result-symbol success"><CheckCircle2 size={31} /></span>
            <p>{checkout.order.subject}已生效，{checkout.order.durationDays} 天内可使用 {checkout.order.quotaTotal} 次生成额度。</p>
            <Link className="button button-primary button-full" to="/generate">
              开始创作
            </Link>
          </div>
        ) : terminal ? (
          <div className="checkout-terminal">
            <p>该订单已无法继续支付，请关闭后重新下单。</p>
            <button className="button button-secondary button-full" onClick={onClose}>
              关闭
            </button>
          </div>
        ) : (
          <>
            <div className="qr-panel">
              <QRCodeSVG
                value={checkout.paymentPageUrl}
                size={220}
                level="M"
                marginSize={2}
                bgColor="#ffffff"
                fgColor="#0b0e13"
                imageSettings={{
                  src: PAYMENT_QR_LOGO_URL,
                  height: 44,
                  width: 44,
                  excavate: true,
                }}
                title="支付宝付款中间页二维码"
              />
            </div>
            <p className="checkout-amount">
              应付 <strong>¥{formatAmount(checkout.order.amountFen, true)}</strong>
              {testMode && <span>测试环境</span>}
            </p>
            <p className="modal-copy checkout-copy">
              使用支付宝 App 扫描二维码即可进入收银台。也可以在当前设备打开付款页；
              微信内打开时会提示改用支付宝扫码。
            </p>
            <a
              className="button button-primary button-full"
              href={checkout.paymentPageUrl}
              target="_blank"
              rel="noreferrer"
            >
              <CreditCard size={17} />在本机打开付款页
            </a>
            <div className="checkout-status" aria-live="polite">
              {refreshFailed ? (
                "暂时无法刷新订单状态，付款通知仍会由服务端继续处理"
              ) : (
                <><LoaderCircle className="spin" size={14} />等待支付宝确认付款结果</>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Feature({ children }: { children: string }) {
  return <div className="feature"><span><Check size={13} /></span>{children}</div>;
}
